import { CodeStatus, PermitCategory, PermitType, SignatureType } from "../../common/enums";
import { formElement } from "../forms/FormElement";
import ShipRepairsPermitFormState from "../forms/Permits/ShipRepairsPermitFormState";
import Person, { Person_default, Person_toFormElement } from "../Person";
import Position, { Position_toPositionPickerRow } from "../Position";
import Signature, { Signature_empty } from "../Signature";
import { permitContentVersions } from "../WorkPermit";

type ShipRepairsPermit = {
    id: string;
    entityId: number;
    remindMe: boolean;
    remindMeDate: Date;
    startDate: Date;
    endDate: Date;
    permitCategory: PermitCategory.ColdWork;
    sequenceIdentifier: string;
    version: 1;
    hasAttachedFiles: boolean;
    createdByUserName: string;
    renewalOfPermitSequenceIdentifier: string;
    renewedByPermitSequenceIdentifier: string;
    initialStartDate: Date;
    extendedEndDate: Date;
    adminComment: string;
    status: CodeStatus;
    departmentId: number;
    includesMajorRepairs: boolean;
    shipCanBeMovedByOwnMachine: boolean;
    includesRunningPropellers: boolean;
    shipCanBeMovedByTowBoat: boolean;
    hasAgreementWithTowBoatCompany: boolean;
    description: string;
    permitName: PermitType.ShipRepairsPermit;
    positions: Position[];
    expirationReminderEmail: string;
    expirationReminderPhoneNumber: string;
    rejectionComment: string;
    shipName: string;
    callSign: string;
    commanderOrRepresentative: Person;
    anonymousUserEmail: string;
    isAnonymousPermit: boolean;
    permitProviderSignature: Signature;
    contentVersion: number;

    canUpdate: boolean;
    canAccept: boolean;
    canManageAttachments: boolean;
    canManagePositions: boolean;
    canReject: boolean;
    canManageAdminComment: boolean;
    canRenew: boolean;
    canUpdateWithNotification: boolean;
    canManagePositionPolygons: boolean;
};

export default ShipRepairsPermit;

export function ShipRepairsPermit_toFormState(ShipRepairsPermit: ShipRepairsPermit): ShipRepairsPermitFormState {
    const formState: ShipRepairsPermitFormState = {
        adminComment: formElement(ShipRepairsPermit.adminComment),
        anonymousUserEmail: formElement(ShipRepairsPermit.anonymousUserEmail),
        commanderOrRepresentative: Person_toFormElement(ShipRepairsPermit.commanderOrRepresentative),
        description: formElement(ShipRepairsPermit.description),
        endDate: formElement(ShipRepairsPermit.endDate),
        expirationReminderEmail: formElement(ShipRepairsPermit.expirationReminderEmail),
        expirationReminderPhoneNumber: formElement(ShipRepairsPermit.expirationReminderPhoneNumber),
        shipName: formElement(ShipRepairsPermit.shipName),
        callSign: formElement(ShipRepairsPermit.callSign),
        hasAgreementWithTowBoatCompany: formElement(ShipRepairsPermit.hasAgreementWithTowBoatCompany),
        includesMajorRepairs: formElement(ShipRepairsPermit.includesMajorRepairs),
        includesRunningPropellers: formElement(ShipRepairsPermit.includesRunningPropellers),
        positions: ShipRepairsPermit.positions.map(Position_toPositionPickerRow),
        remindMe: formElement(ShipRepairsPermit.remindMe),
        remindMeDate: formElement(ShipRepairsPermit.remindMeDate),
        shipCanBeMovedByOwnMachine: formElement(ShipRepairsPermit.shipCanBeMovedByOwnMachine),
        shipCanBeMovedByTowBoat: formElement(ShipRepairsPermit.shipCanBeMovedByTowBoat),
        startDate: formElement(ShipRepairsPermit.startDate)
    };

    return formState;
}

export function ShipRepairsPermit_normalize(shipRepairsPermit: ShipRepairsPermit): ShipRepairsPermit {
    const normalizedShipRepairsPermit: ShipRepairsPermit = {
        adminComment: shipRepairsPermit.adminComment ?? "",
        anonymousUserEmail: shipRepairsPermit.anonymousUserEmail ?? "",
        canAccept: shipRepairsPermit.canAccept ?? false,
        callSign: shipRepairsPermit.callSign ?? "",
        canManageAdminComment: shipRepairsPermit.canManageAdminComment ?? false,
        canManageAttachments: shipRepairsPermit.canManageAttachments ?? false,
        canManagePositions: shipRepairsPermit.canManagePositions ?? false,
        canReject: shipRepairsPermit.canReject ?? false,
        canUpdate: shipRepairsPermit.canUpdate ?? false,
        commanderOrRepresentative: shipRepairsPermit.commanderOrRepresentative ?? Person_default(),
        createdByUserName: shipRepairsPermit.createdByUserName ?? "",
        departmentId: shipRepairsPermit.departmentId ?? 0,
        description: shipRepairsPermit.description ?? "",
        endDate: shipRepairsPermit.endDate ? new Date(shipRepairsPermit.endDate) : new Date(),
        entityId: shipRepairsPermit.entityId ?? 0,
        expirationReminderEmail: shipRepairsPermit.expirationReminderEmail ?? "",
        expirationReminderPhoneNumber: shipRepairsPermit.expirationReminderPhoneNumber ?? "",
        extendedEndDate: shipRepairsPermit.extendedEndDate ? new Date(shipRepairsPermit.extendedEndDate) : new Date(),
        hasAttachedFiles: shipRepairsPermit.hasAttachedFiles ?? false,
        hasAgreementWithTowBoatCompany: shipRepairsPermit.hasAgreementWithTowBoatCompany ?? false,
        id: shipRepairsPermit.id ?? "",
        includesMajorRepairs: shipRepairsPermit.includesMajorRepairs ?? false,
        includesRunningPropellers: shipRepairsPermit.includesRunningPropellers ?? false,
        initialStartDate: shipRepairsPermit.initialStartDate
            ? new Date(shipRepairsPermit.initialStartDate)
            : new Date(),
        isAnonymousPermit: shipRepairsPermit.isAnonymousPermit ?? false,
        permitCategory: shipRepairsPermit.permitCategory ?? PermitCategory.ColdWork,
        permitName: PermitType.ShipRepairsPermit,
        positions: shipRepairsPermit.positions ?? [],
        remindMe: shipRepairsPermit.remindMe ?? false,
        remindMeDate: shipRepairsPermit.remindMeDate ? new Date(shipRepairsPermit.remindMeDate) : new Date(),
        sequenceIdentifier: shipRepairsPermit.sequenceIdentifier ?? "",
        shipCanBeMovedByOwnMachine: shipRepairsPermit.shipCanBeMovedByOwnMachine ?? false,
        shipCanBeMovedByTowBoat: shipRepairsPermit.shipCanBeMovedByTowBoat ?? false,
        shipName: shipRepairsPermit.shipName ?? "",
        status: shipRepairsPermit.status ?? CodeStatus.Registered,
        renewalOfPermitSequenceIdentifier: shipRepairsPermit.renewalOfPermitSequenceIdentifier ?? "",
        renewedByPermitSequenceIdentifier: shipRepairsPermit.renewedByPermitSequenceIdentifier ?? "",
        rejectionComment: shipRepairsPermit.rejectionComment ?? "",
        version: shipRepairsPermit.version ?? 1,
        permitProviderSignature:
            shipRepairsPermit.permitProviderSignature ?? Signature_empty(SignatureType.PermitProvider),
        startDate: shipRepairsPermit.startDate ? new Date(shipRepairsPermit.startDate) : new Date(),
        canRenew: shipRepairsPermit.canRenew ?? false,
        canUpdateWithNotification: shipRepairsPermit.canUpdateWithNotification ?? false,
        canManagePositionPolygons: shipRepairsPermit.canManagePositionPolygons ?? false,
        contentVersion: shipRepairsPermit.contentVersion ?? Math.max(...Object.values(permitContentVersions))
    };

    return normalizedShipRepairsPermit;
}
